import { AnswerForType, FormFieldOptions } from "@tellescope/types-models";
import { useCallback } from "react";
import { convertFormFieldType, normalizeFieldOptions } from "../../utils";
import { FormTypesWithValues } from "../../types";
import type { useTellescopeFormCore } from "../use-tellescope-form-core/use-tellescope-form-core";
import { VALIDATE_ON_CHANGE_FIELD_TYPES } from "./validate-on-change-field-types";

export interface UseGetFieldPropsArgs {
  core: ReturnType<typeof useTellescopeFormCore>;
}

export const useGetFieldProps = ({ core }: UseGetFieldPropsArgs) =>
  useCallback(
    <TField extends FormTypesWithValues = FormTypesWithValues>(
      fieldId: string,
      type: TField
    ): {
      id: string;
      type: TField;
      title: string;
      value: AnswerForType[TField];
      onChange: (newValue: AnswerForType[TField]) => void;
      onBlur?: () => void;
      error: string | null;
      touched: boolean;
      options?: FormFieldOptions;
      required?: boolean;
      fullZIP?: boolean;
      disabled?: boolean;
      pendingFiles?: File[];
      onPendingFilesChange?: (files: File[]) => void;
    } | null => {
      const fieldResponse = core.currentResponses[fieldId];
      const fieldMetadata = core.visibleFields.find((f) => f.id === fieldId) ?? core.fields?.fieldsMap[fieldId];

      if (!fieldResponse || !fieldMetadata) {
        console.warn(`Field ${fieldId} not found in form responses or metadata`);
        return null;
      }

      if (type !== convertFormFieldType(fieldMetadata.type)) {
        return null;
      }

      const touched = core.touchedFields.has(fieldId);

      const onChange = (newValue: AnswerForType[TField]) => {
        core.setFieldValue(fieldId, newValue);
        if (VALIDATE_ON_CHANGE_FIELD_TYPES.has(type) || (touched && core.fieldErrors[fieldId])) {
          core.validateField({ fieldId, valueOverride: newValue });
        }
      };

      const onBlur = () => core.handleFieldBlur(fieldId);

      return {
        id: fieldId,
        type,
        title: fieldMetadata.title,
        value: fieldResponse.answer.value as AnswerForType[TField],
        onChange,
        onBlur,
        error: touched ? core.fieldErrors[fieldId] ?? null : null,
        touched,
        options: normalizeFieldOptions(fieldMetadata.options),
        required: fieldMetadata.isOptional === false,
        fullZIP: fieldMetadata.fullZIP,
        disabled: fieldResponse.disabled,
        pendingFiles: core.pendingFiles[fieldId],
        onPendingFilesChange: (files: File[]) => core.setPendingFilesForField(fieldId, files),
      };
    },
    [core]
  );
